import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { money, dateFmt } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "./ui/dialog";
import { HandCoins, Plus, PencilSimple, Trash, ListNumbers, MagnifyingGlass, FilePdf, Coins, FileXls, EnvelopeSimple, Receipt, Paperclip, UploadSimple, Eye, WhatsappLogo, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { LinkClientButton } from "./LinkClientButton";

const empty = { first_name: "", last_name: "", address: "", phone: "", email: "", principal: "", annual_rate: "", term_months: "", currency: "DOP" };

const DocumentsDialog = ({ loan, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const [docs, setDocs] = useState(loan.documents || []);
  const [dirty, setDirty] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => { if (open) { setDocs(loan.documents || []); setDirty(false); } }, [open]); // eslint-disable-line

  const closeAndRefresh = (o) => { setOpen(o); if (!o && dirty) onDone(); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      const { data } = await api.post(`/admin/loan-records/${loan.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, data]); setLabel(""); setDirty(true);
      toast.success(t("document_uploaded"));
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeDoc = async (docId) => {
    setBusy(true);
    try { await api.delete(`/admin/loan-records/${loan.id}/documents/${docId}`); setDocs((d) => d.filter((x) => x.id !== docId)); setDirty(true); toast.success("OK"); }
    catch (err) { toast.error("Error"); }
    finally { setBusy(false); }
  };

  const count = docs.length;

  return (
    <Dialog open={open} onOpenChange={closeAndRefresh}>
      <DialogTrigger asChild>
        <Button data-testid={`docs-loan-${loan.id}`} title={t("documents")} aria-label={t("documents")} size="sm" variant="outline" className={(loan.documents || []).length ? "border-emerald-400 text-emerald-700 hover:bg-emerald-50 gap-1" : "border-slate-300 text-slate-500 hover:bg-slate-50 gap-1"}>
          <Paperclip size={15} weight="bold" />{(loan.documents || []).length > 0 && <span className="text-xs font-bold">{(loan.documents || []).length}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="documents-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("documents")} — {loan.loan_number}</DialogTitle>
          <DialogDescription>{t("documents_hint")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label>{t("document_label")}</Label><Input data-testid="doc-label" className="mt-1 h-10" placeholder={t("document_label_ph")} value={label} onChange={(e) => setLabel(e.target.value)} /></div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="doc-file-input" />
          <Button data-testid="doc-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 h-10 gap-1.5"><UploadSimple size={16} weight="bold" /> {t("upload")}</Button>
        </div>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-100 divide-y">
          {count === 0 ? (
            <p className="text-center text-slate-400 py-6 text-sm">{t("no_documents")}</p>
          ) : docs.map((d) => (
            <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center justify-between px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <FilePdf size={18} weight="duotone" className="text-emerald-600 flex-shrink-0" />
                <div className="min-w-0"><p className="text-sm font-semibold text-emerald-900 truncate">{d.label || d.filename}</p><p className="text-[11px] text-slate-400 truncate">{(d.created_at || "").slice(0, 10)}</p></div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <a data-testid={`doc-view-${d.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" title={t("view_document")} aria-label={t("view_document")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Eye size={15} weight="bold" /></Button></a>
                <Button data-testid={`doc-del-${d.id}`} onClick={() => removeDoc(d.id)} disabled={busy} title={t("delete")} aria-label={t("delete")} size="sm" variant="outline" className="border-red-200 text-red-500 hover:bg-red-50"><Trash size={15} weight="bold" /></Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LoanDialog = ({ editing, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF(editing ? {
      ...empty, ...editing,
      principal: String(editing.principal), annual_rate: String(editing.annual_rate), term_months: String(editing.term_months),
    } : empty);
  }, [open]); // eslint-disable-line

  const valid = f.first_name.trim() && f.last_name.trim() && Number(f.principal) > 0 && Number(f.term_months) > 0 && Number(f.annual_rate) >= 0;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: f.first_name, last_name: f.last_name, address: f.address, phone: f.phone, email: f.email,
        principal: Number(f.principal), annual_rate: Number(f.annual_rate), term_months: Number(f.term_months), currency: f.currency,
      };
      if (editing) await api.put(`/admin/loan-records/${editing.id}`, payload);
      else await api.post("/admin/loan-records", payload);
      toast.success("OK"); setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <Button data-testid={`edit-loan-${editing.id}`} title={t("edit")} aria-label={t("edit")} size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50"><PencilSimple size={15} weight="bold" /></Button>
          : <Button data-testid="new-loan-btn" className="bg-amber-600 hover:bg-amber-700 gap-1.5"><Plus size={16} weight="bold" /> {t("new_loan")}</Button>}
      </DialogTrigger>
      <DialogContent data-testid="loan-record-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{editing ? t("edit_loan") : t("new_loan")}</DialogTitle>
          <DialogDescription className="sr-only">{t("new_loan")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t("first_name")}</Label><Input data-testid="ln-first" className="mt-1 h-10" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} /></div>
          <div><Label>{t("last_name")}</Label><Input data-testid="ln-last" className="mt-1 h-10" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} /></div>
          <div><Label>{t("email")}</Label><Input data-testid="ln-email" className="mt-1 h-10" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>{t("phone")}</Label><Input data-testid="ln-phone" className="mt-1 h-10" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
          <div className="col-span-2"><Label>{t("address")}</Label><Input data-testid="ln-address" className="mt-1 h-10" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
          <div><Label>{t("loan_amount_label")}</Label><Input data-testid="ln-principal" type="number" step="0.01" className="mt-1 h-10 font-mono-num" value={f.principal} onChange={(e) => setF({ ...f, principal: e.target.value })} /></div>
          <div><Label>{t("interest_rate")} (%)</Label><Input data-testid="ln-rate" type="number" step="0.01" className="mt-1 h-10 font-mono-num" value={f.annual_rate} onChange={(e) => setF({ ...f, annual_rate: e.target.value })} /></div>
          <div><Label>{t("term_months")}</Label><Input data-testid="ln-term" type="number" step="1" className="mt-1 h-10 font-mono-num" value={f.term_months} onChange={(e) => setF({ ...f, term_months: e.target.value })} /></div>
          <div>
            <Label>{t("currency")}</Label>
            <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
              <SelectTrigger data-testid="ln-currency" className="mt-1 h-10"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="DOP">RD$ (DOP)</SelectItem><SelectItem value="USD">US$ (USD)</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="ln-submit" onClick={submit} disabled={busy || !valid} className="bg-amber-600 hover:bg-amber-700">{busy ? t("loading") : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AmortizationDialog = ({ loan }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`amort-loan-${loan.id}`} title={t("view_schedule")} aria-label={t("view_schedule")} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><ListNumbers size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="amort-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("amortization")} — {loan.loan_number}</DialogTitle>
          <DialogDescription>{loan.full_name} · {money(loan.principal, loan.currency)} @ {loan.annual_rate}% · {loan.term_months} {t("months_unit")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 mb-2">
          <div className="rounded-lg bg-emerald-50 p-3"><p className="text-xs text-slate-500">{t("monthly_payment")}</p><p className="font-mono-num font-bold text-emerald-900" data-testid="amort-monthly">{money(loan.monthly_payment, loan.currency)}</p></div>
          <div className="rounded-lg bg-amber-50 p-3"><p className="text-xs text-slate-500">{t("total_interest")}</p><p className="font-mono-num font-bold text-amber-700">{money(loan.total_interest, loan.currency)}</p></div>
          <div className="rounded-lg bg-slate-50 p-3"><p className="text-xs text-slate-500">{t("total_payment")}</p><p className="font-mono-num font-bold text-slate-800">{money(loan.total_payment, loan.currency)}</p></div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-gray-100">
          <Table>
            <TableHeader className="sticky top-0 bg-white">
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead className="text-right">{t("payment")}</TableHead>
                <TableHead className="text-right">{t("principal")}</TableHead>
                <TableHead className="text-right">{t("interest")}</TableHead>
                <TableHead className="text-right">{t("balance")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(loan.schedule || []).map((row) => (
                <TableRow key={row.period} data-testid={`amort-row-${row.period}`}>
                  <TableCell className="font-semibold text-emerald-800">{row.period}</TableCell>
                  <TableCell className="text-right font-mono-num">{money(row.payment, loan.currency)}</TableCell>
                  <TableCell className="text-right font-mono-num text-emerald-700">{money(row.principal, loan.currency)}</TableCell>
                  <TableCell className="text-right font-mono-num text-amber-700">{money(row.interest, loan.currency)}</TableCell>
                  <TableCell className="text-right font-mono-num">{money(row.balance, loan.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PaymentDialog = ({ loan, onDone }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setAmount(""); }, [open]);

  const submit = async () => {
    const val = Number(amount);
    if (!val || val <= 0) { toast.error(t("invalid_amount")); return; }
    setBusy(true);
    try {
      await api.post(`/admin/loan-records/${loan.id}/pay`, { amount: val });
      toast.success("OK"); setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const downloadReceipt = async (idx) => {
    try {
      const res = await api.get(`/admin/loan-records/${loan.id}/payments/${idx}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `recibo_${loan.loan_number}_${idx + 1}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`pay-loan-${loan.id}`} title={t("register_payment")} aria-label={t("register_payment")} size="sm" variant="outline" disabled={loan.paid_off} className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"><Coins size={15} weight="bold" /></Button>
      </DialogTrigger>
      <DialogContent data-testid="payment-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-emerald-900">{t("register_payment")} — {loan.loan_number}</DialogTitle>
          <DialogDescription>{loan.full_name}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 mb-1">
          <div className="rounded-lg bg-slate-50 p-2"><p className="text-[11px] text-slate-500">{t("total_payment")}</p><p className="font-mono-num font-bold text-slate-800 text-sm">{money(loan.total_payment, loan.currency)}</p></div>
          <div className="rounded-lg bg-emerald-50 p-2"><p className="text-[11px] text-slate-500">{t("total_paid")}</p><p className="font-mono-num font-bold text-emerald-700 text-sm" data-testid="pay-total-paid">{money(loan.total_paid, loan.currency)}</p></div>
          <div className="rounded-lg bg-amber-50 p-2"><p className="text-[11px] text-slate-500">{t("remaining_balance")}</p><p className="font-mono-num font-bold text-amber-700 text-sm" data-testid="pay-remaining">{money(loan.remaining, loan.currency)}</p></div>
        </div>
        <div>
          <Label>{t("mv_amount")} ({loan.currency})</Label>
          <Input data-testid="payment-amount" type="number" step="0.01" min="0" className="mt-1 h-11 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </div>
        {(loan.payments || []).length > 0 && (
          <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-100 mt-1">
            <Table>
              <TableHeader className="sticky top-0 bg-white"><TableRow><TableHead>{t("date")}</TableHead><TableHead className="text-right">{t("payment")}</TableHead><TableHead className="text-right">{t("balance")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {loan.payments.map((p, i) => (
                  <TableRow key={i} data-testid={`payment-row-${i}`}>
                    <TableCell className="text-xs">{(p.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className="text-right font-mono-num text-emerald-700">{money(p.amount, loan.currency)}</TableCell>
                    <TableCell className="text-right font-mono-num">{money(p.remaining_after, loan.currency)}</TableCell>
                    <TableCell className="text-right"><Button data-testid={`receipt-${i}`} title={t("payment_receipt")} aria-label={t("payment_receipt")} size="sm" variant="ghost" onClick={() => downloadReceipt(i)} className="h-7 px-2 text-emerald-700"><Receipt size={15} weight="bold" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="payment-submit" onClick={submit} disabled={busy || loan.paid_off} className="bg-emerald-700 hover:bg-emerald-800">{busy ? t("loading") : t("register_payment")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export const LoansPanel = () => {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reminderDay, setReminderDay] = useState("1");
  const [savingDay, setSavingDay] = useState(false);

  useEffect(() => { api.get("/admin/loan-reminder-settings").then((r) => setReminderDay(String(r.data.day))).catch(() => {}); }, []);

  const saveReminderDay = async () => {
    const d = Number(reminderDay);
    if (!d || d < 1 || d > 31) { toast.error(t("reminder_day_invalid")); return; }
    setSavingDay(true);
    try { await api.put("/admin/loan-reminder-settings", { day: d }); toast.success(t("reminder_day_saved")); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSavingDay(false); }
  };
  const load = () => {
    setLoading(true); setError("");
    return api.get("/admin/loan-records")
      .then((r) => setItems(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete_loan"))) return;
    try { await api.delete(`/admin/loan-records/${id}`); toast.success("OK"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadCert = async (l) => {
    try {
      const res = await api.get(`/admin/loan-records/${l.id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `certificado_prestamo_${l.loan_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/admin/loan-records/export${search ? `?q=${encodeURIComponent(search)}` : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "prestamos.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const notify = async (l) => {
    try { const { data } = await api.post(`/admin/loan-records/${l.id}/notify`); toast.success(data.message || "OK"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const waLink = (l) => {
    const digits = String(l.phone || "").replace(/\D/g, "");
    const num = digits.length === 10 ? "1" + digits : digits;
    const msg = `Grupo Estrella Hernández Inversiones - Aviso de cuota.\nEstimado/a ${l.full_name}, su préstamo ${l.loan_number} tiene un saldo pendiente de ${money(l.remaining, l.currency)} y cuota de ${money(l.monthly_payment, l.currency)}. Le agradecemos ponerse al día. ¡Gracias!`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const q = search.toLowerCase();
  const filtered = items.filter((l) =>
    ((l.loan_number || "").toLowerCase().includes(q) ||
    (l.full_name || "").toLowerCase().includes(q) ||
    (l.email || "").toLowerCase().includes(q) ||
    (l.phone || "").toLowerCase().includes(q)) &&
    (!pendingOnly || !l.paid_off)
  );

  return (
    <div className="space-y-5" data-testid="loans-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <HandCoins size={22} weight="duotone" className="text-amber-600" />
          <h3 className="font-display font-bold text-emerald-900 text-xl">{t("loans_registry")}</h3>
        </div>
        <LoanDialog onDone={load} />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative max-w-sm w-full">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="loans-search" placeholder={t("search_client")} className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-3 h-10" data-testid="reminder-day-control">
          <BellRinging size={16} weight="duotone" className="text-emerald-700" />
          <span className="text-sm text-slate-600 whitespace-nowrap">{t("reminder_day_label")}</span>
          <Input data-testid="reminder-day-input" type="number" min="1" max="31" value={reminderDay} onChange={(e) => setReminderDay(e.target.value)} className="w-16 h-8" />
          <Button data-testid="reminder-day-save" size="sm" onClick={saveReminderDay} disabled={savingDay} className="bg-emerald-800 hover:bg-emerald-900 h-8">{t("save")}</Button>
        </div>
        <div className="flex gap-2">
          <Button data-testid="loans-pending-toggle" onClick={() => setPendingOnly((v) => !v)} variant={pendingOnly ? "default" : "outline"} className={pendingOnly ? "bg-amber-600 hover:bg-amber-700 gap-1.5" : "border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5"}>{t("pending_only")}</Button>
          <Button data-testid="loans-export-btn" onClick={exportExcel} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><FileXls size={16} weight="bold" /> Excel</Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("loan_number")}</TableHead>
              <TableHead>{t("holder")}</TableHead>
              <TableHead className="text-right">{t("loan_amount_label")}</TableHead>
              <TableHead className="text-right">{t("interest_rate")}</TableHead>
              <TableHead className="text-right">{t("monthly_payment")}</TableHead>
              <TableHead className="text-right">{t("remaining_balance")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{t("loading")}</TableCell></TableRow>
            ) : error ? (
              <TableRow><TableCell colSpan={7} className="text-center text-red-500 py-8" data-testid="loans-error">{error}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">—</TableCell></TableRow>
            ) : filtered.map((l) => (
              <TableRow key={l.id} data-testid={`loan-row-${l.id}`}>
                <TableCell className="font-mono-num text-amber-700 font-semibold">{l.loan_number}</TableCell>
                <TableCell>
                  <div className="font-semibold text-emerald-900">{l.full_name}</div>
                  <div className="text-xs text-slate-400">{l.email || l.phone}</div>
                  {l.user_id && l.linked_by && <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`linked-info-${l.id}`}>{t("linked_by")}: {l.linked_by}{l.linked_at ? ` · ${dateFmt(l.linked_at, lang)}` : ""}</div>}
                </TableCell>
                <TableCell className="text-right font-mono-num font-bold text-emerald-900">{money(l.principal, l.currency)}</TableCell>
                <TableCell className="text-right font-mono-num">{l.annual_rate}%</TableCell>
                <TableCell className="text-right font-mono-num text-emerald-700">{money(l.monthly_payment, l.currency)}</TableCell>
                <TableCell className="text-right" data-testid={`loan-remaining-${l.id}`}>
                  {l.paid_off
                    ? <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{t("paid_off")}</span>
                    : <span className="font-mono-num font-bold text-amber-700">{money(l.remaining, l.currency)}</span>}
                  {l.last_notified_at && <div className="text-[10px] text-slate-400 mt-1">{t("last_notice")}: {(l.last_notified_at || "").slice(0, 10)}</div>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-2 justify-end">
                    <PaymentDialog loan={l} onDone={load} />
                    <AmortizationDialog loan={l} />
                    <DocumentsDialog loan={l} onDone={load} />
                    <Button data-testid={`notify-loan-${l.id}`} title={t("send_due_notice")} aria-label={t("send_due_notice")} size="sm" variant="outline" onClick={() => notify(l)} disabled={!l.email || l.paid_off} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 disabled:opacity-40"><EnvelopeSimple size={15} weight="bold" /></Button>
                    {l.phone && !l.paid_off ? (
                      <a data-testid={`wa-loan-${l.id}`} href={waLink(l)} target="_blank" rel="noopener noreferrer" title="WhatsApp" aria-label="WhatsApp" className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-[#25D366] text-white hover:bg-[#1eb85a]"><WhatsappLogo size={16} weight="fill" /></a>
                    ) : (
                      <Button data-testid={`wa-loan-${l.id}`} size="sm" variant="outline" disabled className="border-slate-200 text-slate-300 disabled:opacity-40"><WhatsappLogo size={15} weight="bold" /></Button>
                    )}
                    <Button data-testid={`cert-loan-${l.id}`} title={t("certificate")} aria-label={t("certificate")} size="sm" variant="outline" onClick={() => downloadCert(l)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"><FilePdf size={15} weight="bold" /></Button>
                    <LinkClientButton base="/admin/loan-records" account={{ ...l, account_number: l.loan_number }} onDone={load} />
                    <LoanDialog editing={l} onDone={load} />
                    <Button data-testid={`del-loan-${l.id}`} title={t("delete")} aria-label={t("delete")} size="sm" variant="outline" onClick={() => del(l.id)} className="border-red-200 text-red-500 hover:bg-red-50"><Trash size={15} weight="bold" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
